import dayjs from "dayjs";
import { normalizeDashboardDateRange } from "./dashboardDateRange";

const TAB_LABELS_PT = {
  live: "Ao vivo",
  overview: "Visão Geral",
  clients: "Clientes",
  products: "Produtos",
  sales: "Vendas",
  busTickets: "Passagens",
  surveys: "Pesquisas",
};

/** Parses internal dashboard notification/deep links. */
export function parseDashboardDeepLink(deepLink) {
  if (!deepLink || typeof deepLink !== "string") return null;
  try {
    const url = new URL(deepLink, window.location.origin);
    const partnerMatch = url.pathname.match(/\/partner\/console\/dashboard\/([^/?]+)/);
    const workspaceMatch = url.pathname.match(/\/dashboard\/([^/?]+)/);
    const tabKey = partnerMatch?.[1] || workspaceMatch?.[1] || null;
    const isPartner = Boolean(partnerMatch);
    const start = url.searchParams.get("start");
    const end = url.searchParams.get("end");
    const surveyId = url.searchParams.get("survey");
    const cacheExpRaw = url.searchParams.get("cache_exp");
    let cacheExpiresAt = null;
    if (cacheExpRaw) {
      const parsed = new Date(cacheExpRaw);
      if (!Number.isNaN(parsed.getTime())) cacheExpiresAt = parsed.toISOString();
    }
    return {
      tabKey,
      tabLabel: tabKey ? TAB_LABELS_PT[tabKey] || tabKey : null,
      isPartner,
      start,
      end,
      surveyId,
      jobId: url.searchParams.get("job"),
      cacheExpiresAt,
      pathname: url.pathname,
      search: url.search,
    };
  } catch {
    return null;
  }
}

export function formatDashboardPeriodLabel(start, end, locale = "pt-BR") {
  const s = start ? dayjs(start, "YYYY-MM-DD", true) : null;
  const e = end ? dayjs(end, "YYYY-MM-DD", true) : null;
  if (s?.isValid() && e?.isValid()) {
    return `${s.locale(locale).format("L")} – ${e.locale(locale).format("L")}`;
  }
  if (s?.isValid()) return s.locale(locale).format("L");
  if (e?.isValid()) return e.locale(locale).format("L");
  return "";
}

/** Reads dashboard cache expiry from notification deep link. */
export function readDashboardCacheExpiry(deepLink, nowMs = Date.now()) {
  const parsed = parseDashboardDeepLink(deepLink);
  if (!parsed?.cacheExpiresAt) return null;
  const expiresAtMs = new Date(parsed.cacheExpiresAt).getTime();
  if (!Number.isFinite(expiresAtMs)) return null;
  return {
    expiresAt: parsed.cacheExpiresAt,
    expiresAtMs,
    isExpired: nowMs >= expiresAtMs,
  };
}

export function formatDashboardCacheExpiresLabel(iso, locale = "pt-BR") {
  const d = dayjs(iso);
  if (!d.isValid()) return "";
  return d.locale(locale).format("L LT");
}

/**
 * Subtitle lines for dashboard report notifications (period + cache validity).
 * Strips embedded "Válido até …" from stored body when recomputing live expiry.
 */
export function dashboardNotificationSubtitle(item, locale = "pt-BR", nowMs = Date.now()) {
  let body = String(item?.body || "").trim();
  body = body.replace(/\s*·\s*Válido até\s+[\d/]+\s+[\d:]+$/i, "").trim();
  body = body.replace(/\s*·\s*Valid until\s+[\d/]+\s+[\d:]+$/i, "").trim();

  const parsed = parseDashboardDeepLink(item?.deepLink);
  const cache = readDashboardCacheExpiry(item?.deepLink, nowMs);

  const parts = [];
  if (body) {
    parts.push(body);
  } else if (parsed) {
    const period = formatDashboardPeriodLabel(parsed.start, parsed.end, locale);
    if (parsed.tabLabel) parts.push(`Aba ${parsed.tabLabel}`);
    if (period) parts.push(`Período ${period}`);
  }

  if (cache) {
    if (cache.isExpired) {
      parts.push("__CACHE_EXPIRED__");
    } else {
      const label = formatDashboardCacheExpiresLabel(cache.expiresAt, locale);
      if (label) parts.push(`__CACHE_VALID__${label}`);
    }
  }

  return parts.join(" · ");
}

export function splitDashboardNotificationSubtitle(subtitle, t) {
  const raw = String(subtitle || "");
  if (!raw) return { text: "", cacheExpired: false, cacheValidUntil: null };

  const segments = raw.split(" · ").filter(Boolean);
  const cacheExpired = segments.includes("__CACHE_EXPIRED__");
  const validSeg = segments.find((s) => s.startsWith("__CACHE_VALID__"));
  const cacheValidUntil = validSeg ? validSeg.replace("__CACHE_VALID__", "") : null;

  const main = segments
    .filter((s) => s !== "__CACHE_EXPIRED__" && !s.startsWith("__CACHE_VALID__"))
    .join(" · ");

  const cacheLine = cacheExpired
    ? t("bell.dashboardCacheExpired", { defaultValue: "Cache expirado — toque para atualizar" })
    : cacheValidUntil
      ? t("bell.dashboardCacheValidUntil", {
          date: cacheValidUntil,
          defaultValue: "Válido até {{date}}",
        })
      : null;

  const text = [main, cacheLine].filter(Boolean).join(" · ");
  return { text, cacheExpired, cacheValidUntil, cacheLine };
}

/**
 * Applies start/end from URL search params to dashboard date state.
 * Returns true when at least one valid date was applied.
 */
export function applyDashboardDatesFromSearchParams(searchParams, { onApply }) {
  if (!searchParams || typeof onApply !== "function") return false;

  const startRaw = searchParams.get("start");
  const endRaw = searchParams.get("end");
  if (!startRaw && !endRaw) return false;

  const start = startRaw ? dayjs(startRaw, "YYYY-MM-DD", true) : null;
  const end = endRaw ? dayjs(endRaw, "YYYY-MM-DD", true) : null;
  if (!start?.isValid() && !end?.isValid()) return false;

  const nextStart = start?.isValid() ? start.startOf("day") : null;
  const nextEnd = end?.isValid() ? end.endOf("day") : null;
  const normalized = normalizeDashboardDateRange(nextStart, nextEnd);

  onApply(normalized.start, normalized.end, normalized.adjusted);
  return true;
}

function parseDominioIdsParam(raw) {
  if (raw == null || raw === "") return null;
  const ids = String(raw)
    .split(",")
    .map((x) => Number(String(x).trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
  return ids;
}

/**
 * Applies workspace filter from URL (?dominio_ids=1,2).
 * Returns true when param is present (including empty selection via explicit ids).
 */
export function applyDashboardWorkspacesFromSearchParams(searchParams, workspaceOptions, setSelectedWorkspaces) {
  if (!searchParams || typeof setSelectedWorkspaces !== "function") return false;
  if (!searchParams.has("dominio_ids")) return false;

  const ids = parseDominioIdsParam(searchParams.get("dominio_ids"));
  if (!ids) {
    setSelectedWorkspaces([]);
    return true;
  }

  const options = Array.isArray(workspaceOptions) ? workspaceOptions : [];
  if (!options.length) return false;

  const idSet = new Set(ids);
  setSelectedWorkspaces(options.filter((w) => idSet.has(Number(w.id))));
  return true;
}

export function appendDominioIdsToSearchParams(params, dominioIds) {
  const ids = (Array.isArray(dominioIds) ? dominioIds : [])
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (!ids.length) return params;
  return { ...params, dominio_ids: ids.map(String).join(",") };
}

/** Ensures navigation triggers a refresh even when route is already active. */
export function withDashboardNavigationNonce(deepLink) {
  if (!deepLink) return deepLink;
  try {
    const url = new URL(deepLink, window.location.origin);
    url.searchParams.set("_d", String(Date.now()));
    return `${url.pathname}${url.search}`;
  } catch {
    return deepLink;
  }
}
